import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Video } from "lucide-react";
import { api } from "@/lib/api";

export const useMeetings = (state, electorate) =>
  useQuery({
    queryKey: ["meetings", state || "", electorate || ""],
    queryFn: () => api.get("/meetings", { params: { state, electorate } }).then((r) => r.data),
  });

const TYPE_STYLE = {
  national: "bg-[#E2705A]/15 text-[#E2705A]",
  state: "bg-[#8FA58A]/20 text-[#5A7355]",
  division: "bg-[#1B2A41]/10 text-[#1B2A41]",
};

export const MeetingRow = ({ m, dark }) => (
  <li data-testid={`meeting-${m.type}`} className={`flex items-start gap-3 py-2.5 ${dark ? "border-[#FDFBF7]/10" : "border-[#D8D2C4]"} border-b last:border-0`}>
    <span className={`mt-0.5 text-[10px] font-type uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${dark ? "bg-[#FDFBF7]/10 text-[#FDFBF7]" : TYPE_STYLE[m.type]}`}>{m.label}</span>
    <span className={`text-sm flex-1 ${dark ? "text-[#FDFBF7]" : "text-[#1E2830]"}`}>{m.display}</span>
    {m.link && (
      <a href={m.link} target="_blank" rel="noopener noreferrer" data-testid="meeting-join-link" className="text-[#E2705A] hover:underline text-xs inline-flex items-center gap-1">
        <Video className="w-3 h-3" /> Join
      </a>
    )}
  </li>
);

export const NextMeetings = ({ state, electorate, dark }) => {
  const { data } = useMeetings(state, electorate);
  if (!data) return null;
  const items = [...(data.national || []).slice(0, 1), ...(data.state || []).slice(0, 1), ...(data.division || []).slice(0, 1)].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  if (!items.length) return null;
  return (
    <div data-testid="next-meetings">
      <p className={`font-type text-[11px] uppercase tracking-[0.25em] mb-1 ${dark ? "text-[#8FA58A]" : "text-[#5A7355]"}`}>Next meetings</p>
      <ul>{items.map((m) => <MeetingRow key={m.rule_id + m.starts_at} m={m} dark={dark} />)}</ul>
    </div>
  );
};

export const MeetingsSection = () => {
  const { data } = useMeetings();
  if (!data) return null;
  return (
    <section className="bg-[#1B2A41] text-[#FDFBF7] relative overflow-hidden" data-testid="meetings-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[1fr_1.1fr] gap-10">
        <div>
          <p className="font-type text-xs uppercase tracking-[0.3em] text-[#8FA58A] mb-2">How we meet</p>
          <h2 className="font-type font-bold text-3xl sm:text-4xl text-[#E2705A] leading-tight">Fortnightly, alternating.</h2>
          <p className="mt-4 text-sm sm:text-base text-[#FDFBF7]/80 leading-relaxed max-w-prose">
            One fortnight every state meets on its own. The next, everybody is on the one call. Each division also gets a monthly check-in. Times are shown in your local zone on your electorate page.
          </p>
          <ul className="mt-6 space-y-3" data-testid="meeting-rules">
            {(data.rules || []).map((r) => (
              <li key={r.id} className="flex gap-3 items-start">
                <CalendarDays className="w-4 h-4 mt-1 text-[#E2705A] flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm">{r.label}</p>
                  <p className="text-xs text-[#FDFBF7]/60">{r.description}{r.notes ? ` — ${r.notes}` : ""}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl bg-[#121E2B] border border-[#FDFBF7]/10 p-6">
          <p className="font-type text-[11px] uppercase tracking-[0.25em] text-[#8FA58A] mb-2">Upcoming national calls</p>
          <ul>{(data.national || []).map((m) => <MeetingRow key={m.starts_at} m={m} dark />)}</ul>
          <p className="mt-4 text-xs text-[#FDFBF7]/50">State and division dates: open your electorate from the map below.</p>
        </div>
      </div>
    </section>
  );
};
